/**
 * Admin Service
 * 
 * This service handles all admin-specific API calls.
 * It uses the privateApiClient for authenticated requests.
 */

import { privateApiClient, handleApiError } from '../apiClient';
import API from '../../config/api';

// Base URL for admin endpoints
const BASE_ADMIN_URL = `${API.BASE_URL}/admin`;

// Admin API endpoints
const ADMIN_ENDPOINTS = {
  users: {
    list: `${BASE_ADMIN_URL}/users`,
    create: `${BASE_ADMIN_URL}/users`,
    update: (id) => `${BASE_ADMIN_URL}/users/${id}`,
    delete: (id) => `${BASE_ADMIN_URL}/users/${id}`,
    resetPassword: (id) => `${BASE_ADMIN_URL}/users/${id}/reset-password`,
  },
  stats: {
    nominations: `${API.BASE_URL}/nomination-stats`
  }
};

/**
 * Get all users (admin endpoint)
 * @returns {Promise<Array>} List of users
 */
export const getAllUsers = async () => {
  try {
    const response = await privateApiClient.get(ADMIN_ENDPOINTS.users.list);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to fetch users');
  }
};

/**
 * Create a new user (admin endpoint)
 * @param {Object} userData - User data to create
 * @returns {Promise<Object>} Created user data
 */
export const createUser = async (userData) => {
  try {
    const response = await privateApiClient.post(ADMIN_ENDPOINTS.users.create, userData);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to create user');
  }
};

/**
 * Update user (admin endpoint)
 * @param {string} id - User ID
 * @param {Object} userData - Updated user data
 * @returns {Promise<Object>} Updated user data
 */
export const updateUser = async (id, userData) => {
  try {
    const response = await privateApiClient.put(ADMIN_ENDPOINTS.users.update(id), userData);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to update user');
  }
};

/**
 * Delete user (admin endpoint)
 * @param {string} id - User ID
 * @returns {Promise<Object>} Response data
 */
export const deleteUser = async (id) => {
  try {
    const response = await privateApiClient.delete(ADMIN_ENDPOINTS.users.delete(id));
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to delete user');
  }
};

/**
 * Reset user password (admin endpoint)
 * @param {string} id - User ID
 * @param {string} newPassword - New password
 * @returns {Promise<Object>} Response data
 */
export const resetUserPassword = async (id, newPassword) => {
  try {
    const response = await privateApiClient.post(ADMIN_ENDPOINTS.users.resetPassword(id), { newPassword });
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to reset user password');
  }
};

/**
 * Get nomination statistics (admin endpoint)
 * @param {Object} filters - Optional filters (center_id, start_date, end_date)
 * @returns {Promise<Object>} Statistics data
 */
export const getNominationStats = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    // Add filters as query parameters
    if (filters.center_id) {
      params.append('center_id', filters.center_id);
    }
    if (filters.start_date) {
      params.append('start_date', filters.start_date);
    }
    if (filters.end_date) {
      params.append('end_date', filters.end_date);
    }
    
    // Use direct URL without relying on baseURL
    const apiUrl = process.env.REACT_APP_API_URL || 
      (process.env.NODE_ENV === 'production' 
        ? 'https://majalisna-61c919e79d71.herokuapp.com/api/v1' 
        : 'http://localhost:8001/api/v1');
    
    const fullUrl = params.toString() 
      ? `${apiUrl}/nomination-stats?${params.toString()}` 
      : `${apiUrl}/nomination-stats`;
      
    const response = await privateApiClient.get(fullUrl, { baseURL: '' });
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to fetch nomination statistics');
  }
};

// Export all functions as a single object
const adminService = {
  getAllUsers,
  createUser,
  updateUser,
  deleteUser,
  resetUserPassword,
  getNominationStats
};

export default adminService;
